import { MessageList, MessageAffix } from '@halo/ui-chat-react';
import type { SessionStore } from '@/store/client';
import { useMessagesList } from '@/store/client';
import classes from './index.module.scss';

export interface SessionMsgListProps {
  children: React.ReactNode;
  sessionStore: SessionStore;
}

export function SessionMsgList({ children, sessionStore }: SessionMsgListProps) {
  const [readToBottom, setReadToBottom] = useState(false);

  const messageListRef = useRef<HTMLElement>(null);
  const { wrapperRef, listRef, topRef, bottomRef } = useMessagesList(sessionStore);

  const mentions = sessionStore?.session?.at_message_list;

  const lastMessage = sessionStore?.session?.last_message;

  const handleScroll = useCallback(
    (scrollTop: number) => {
      const isTarget = scrollTop > 500;
      if (isTarget && !readToBottom) {
        setReadToBottom(isTarget);
      }
      if (!isTarget && readToBottom) {
        setReadToBottom(isTarget);
      }
    },
    [readToBottom]
  );

  const onBottomClick = useCallback(() => {
    if (lastMessage) {
      sessionStore?.scrollToMessage(lastMessage);
    }
  }, [lastMessage, sessionStore]);

  const onMentionClick = useCallback(async () => {
    const lastMentionMessageId = mentions?.[0];
    if (lastMentionMessageId && sessionStore) {
      const lastMentionMessage = await sessionStore.client.Api.messages.getMessageByCliMsgId(
        sessionStore.session!.chat_id,
        lastMentionMessageId
      );
      sessionStore?.scrollToMessage(lastMentionMessage, true);
    }
  }, [sessionStore, mentions]);

  const [firstVisibleDom, setFirstVisibleDom] = useState<HTMLElement | null>(null);

  // Handle messages list height change
  const stickyObserver = useMemo(() => {
    const domsSet = new Set<HTMLElement>();
    const intersectingSet = new Set<HTMLElement>();
    const ro = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            intersectingSet.add(entry.target as HTMLElement);
          } else {
            intersectingSet.delete(entry.target as HTMLElement);
          }
        }

        const msgDomList = getMsgDomList();
        if (!msgDomList) {
          return;
        }

        let stickyDom_: HTMLElement | null = null;

        for (const msgDom of msgDomList) {
          if (intersectingSet.has(msgDom)) {
            stickyDom_ = msgDom;
            break;
          }
        }
        setFirstVisibleDom(stickyDom_);
      },
      {
        threshold: 0,
      }
    );

    function getMsgDomList() {
      const listDom = listRef.current;
      if (!listDom) {
        return;
      }
      return listDom.querySelectorAll<HTMLElement>('.MessageItem');
    }

    function observe(msgDom: HTMLElement) {
      if (domsSet.has(msgDom)) {
        return;
      }
      const oldHeight = msgDom.getBoundingClientRect().height;
      Object.assign(msgDom, { oldHeight });
      domsSet.add(msgDom);
      ro.observe(msgDom);
    }

    function unobserve(msgDom: HTMLElement) {
      if (!msgDom.isConnected) {
        domsSet.delete(msgDom);
        ro.unobserve(msgDom);
      }
    }

    function handler() {
      const msgDomList = getMsgDomList();
      if (!msgDomList) {
        return;
      }
      msgDomList.forEach(stickyObserver.observe);
      return () => {
        msgDomList.forEach(stickyObserver.unobserve);
      };
    }

    return {
      ro,
      observe,
      unobserve,
      handler,
    };
  }, [listRef]);

  useEffect(() => {
    return stickyObserver.handler();
  }, [sessionStore.enhancedMessages, stickyObserver]);

  useEffect(() => {
    if (firstVisibleDom) {
      let stickyDom_ = firstVisibleDom;
      while (stickyDom_) {
        if (stickyDom_.classList.contains('MessageDividerDate')) {
          break;
        }
        stickyDom_ = stickyDom_.previousElementSibling as HTMLElement;
      }

      if (stickyDom_) {
        requestAnimationFrame(() => {
          stickyDom_.classList.add(classes['sticky-dom']);
        });

        return () => {
          stickyDom_.classList.remove(classes['sticky-dom']);
        };
      }
    }
  }, [firstVisibleDom, sessionStore.enhancedMessages.length, wrapperRef]);

  return (
    <MessageList
      messageListRef={messageListRef}
      wrapperRef={wrapperRef}
      listRef={listRef}
      topRef={topRef}
      bottomRef={bottomRef}
      onScroll={handleScroll}
      messageAffix={
        <MessageAffix
          mentions={mentions?.length ?? 0}
          readToBottom={readToBottom}
          unreadCount={sessionStore?.session?.unread_count ?? 0}
          onMentionClick={onMentionClick}
          onUnreadClick={onBottomClick}
          onBottomClick={onBottomClick}
        />
      }
      borderRadius="lg"
    >
      {children}
    </MessageList>
  );
}
